import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  model,
  NgZone,
  signal,
  ViewChild,
} from '@angular/core';
import { PlayerSourceService } from '@shared/components/player-bar/player-source.service';
import WaveSurfer from 'wavesurfer.js';
import { MatIcon } from '@angular/material/icon';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { SecondsToTimePipe } from '@shared/pipes/secondsToMinutes';
import { NgClass } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';

import { fetchFile } from '@ffmpeg/util';
import {
  Platform,
} from '@angular/cdk/platform';

@Component({
  selector: 'app-player-bar',
  imports: [
    MatIcon,
    MatSlider,
    FormsModule,
    MatSliderThumb,
    NgClass,
    MatProgressBar,
  ],
  templateUrl: './player-bar.html',
  styleUrl: './player-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerBar implements AfterViewInit {
  source = inject(PlayerSourceService)
  platform = inject(Platform);
  private zone = inject(NgZone);

  @ViewChild('player') player!: ElementRef;

  volume = model(100);

  isNativeMode = signal(false);
  progress = signal(0);
  isTrackLoading = signal(true);

  currentTime = signal('0:00');
  duration = signal('');

  private cachedDuration = 0;

  constructor() {

    effect(() => {
      const vol = this.volume();
      this.wavesurfer?.setVolume(vol / 100);
    });

    effect(() => {
      if (!this.source.currentTrack()) {
        return;
      }
      if (!this.wavesurfer) {
        return;
      }

      this.doPlay(true);
    });

  }

  init() {
    this.zone.runOutsideAngular(() => {
      this.wavesurfer = WaveSurfer.create({
        height: 30,
        barWidth: 2,
        barGap: 1,
        container: this.player.nativeElement as HTMLElement,
        backend: 'MediaElement',
        waveColor: '#939691',
        progressColor: '#00FF22',
        blobMimeType: 'audio/mpeg',
      });

      this.wavesurfer.on('ready', () => {
        this.cachedDuration = this.wavesurfer!.getDuration();
      });

      this.wavesurfer.on('audioprocess', () => {
        const t = this.wavesurfer!.getCurrentTime();

        const nextTime = formatTime(t);
        if (this.currentTime() !== nextTime) {
          this.currentTime.set(nextTime);
        }

        const nextProgress = this.cachedDuration
          ? Math.floor((t / this.cachedDuration) * 100)
          : 0;
        if (this.progress() !== nextProgress) {
          this.progress.set(nextProgress);
        }
      });
    });

    this.doPlay(true);
  }

  setTrackPosition(percent: number) {
    const t = Math.floor((percent / 100) * this.wavesurfer!.getDuration())
    this.wavesurfer?.setTime(t);
  }

  ngAfterViewInit(): void {
    this.init();
  }

  wavesurfer: WaveSurfer | null = null;

  play() {
    if (!this.wavesurfer || this.isTrackLoading()) {
      return;
    }
    if (this.source.isPlaying()) {
      this.wavesurfer.pause();
      this.source.pause();
    } else {
      this.wavesurfer.play();
      this.source.play();
    }
  }


  nextTrack() {
    if (!this.wavesurfer) {
      return;
    }
    this.progress.set(0);
    this.source.nextTrack();
  }

  prevTrack() {
    if (!this.wavesurfer) {
      return;
    }
    this.progress.set(0);
    this.source.prevTrack();
  }

  async doPlay(force = false) {
    if (!this.wavesurfer) {
      return;
    }

    this.isNativeMode.set(false);
    this.progress.set(0);
    this.isTrackLoading.set(true);
    if (this.source.isSafari) {

      if (this.source.isMetaCleaningEnabled) {
        try {
          await this.cleanMeta(this.source.currentTrack()!.url);
        } catch (e) {
          try {
            console.log(e);
            this.isNativeMode.set(true);
            await this.wavesurfer!.load(this.source.currentTrack()!.url);
          } catch (e) {
            console.log(e);
          }
        }

      } else {
        try {
          this.isNativeMode.set(true);
          await this.wavesurfer!.load(this.source.currentTrack()!.url);
        } catch (e) {
          console.log(e);
        }
      }

    } else {
      try {
        await this.wavesurfer!.load(
          this.source.currentTrack()!.url,
          this.source.currentTrack()!.waveform,
        );
      } catch (e) {
        console.log(e);
        this.isNativeMode.set(true);
      }

    }
    const duration = this.wavesurfer!.getDuration();
    this.cachedDuration = duration;
    this.wavesurfer.setTime(0);

    if (force || this.source.isPlaying()) {
      await this.wavesurfer!.play();
      this.source.play();
    }

    this.duration.set(new SecondsToTimePipe().transform(duration));

    // in case of accidental stop after loading
    setTimeout(() => {
      if (!this.wavesurfer?.isPlaying() && (force || this.source.isPlaying())) {
        this.wavesurfer!.play();
        this.source.play();
      }
      this.isTrackLoading.set(false);

    }, 500)

  }

  async cleanMeta(trackUrl: string) {
    console.time("cleaning");

    await this.source.ffmpeg.writeFile('input.mp3', await fetchFile(trackUrl));

    await this.source.ffmpeg.exec(['-i', 'input.mp3', '-map_metadata', '-1', '-c:a', 'copy', 'output.mp3']);
    console.log('player-bar.ts => cleanMeta: exec complete', );

    const fileData = await this.source.ffmpeg.readFile("output.mp3");
    console.log('player-bar.ts => cleanMeta: readFile complete', fileData);
    const data = new Uint8Array(fileData as ArrayBuffer);
    console.log('player-bar.ts => cleanMeta: Uint8Array done', data);

    const blob = new Blob([data.buffer], { type: 'audio/mpeg' });
    console.log('player-bar.ts => cleanMeta: blob created', blob);

    await this.wavesurfer!.loadBlob(blob);
    console.timeEnd("cleaning");
  }

  closePlayer() {
    this.wavesurfer?.destroy();
    this.source.clear();
  }
}

function formatTime(time: number): string {
  return [
    Math.floor((time % 3600) / 60),
    ('00' + Math.floor(time % 60)).slice(-2),
  ].join(':');
}
